/**
 * Shared locale resolution for caregiver-facing error screens.
 *
 * Spec 051 (error-screen-parity), Phase 3 / FR-008 + FR-010.
 *
 * Error-screen copy MUST survive the application's failure, so it cannot depend
 * on the application's translation machinery (ngx-translate loads locale JSON
 * over HTTP at runtime — which is exactly what may be unavailable when an error
 * screen is needed). This mirrors the self-contained treatment established by
 * spec 049 on the fatal screen: each screen ships its own `en`/`es` copy map
 * with an English fallback, and resolves the active locale from progressively
 * weaker, failure-tolerant sources.
 *
 * Every reader is wrapped so this helper can never itself throw into the global
 * error handler (which would route to another error screen — FR-010).
 */
export type ErrorScreenLocale = 'en' | 'es';

/** DOM attribute set by the landing shell to pin a language before Angular boots. */
export const ERROR_SCREEN_LANGUAGE_ATTRIBUTE = 'data-rettx-language';
/** localStorage key holding the caregiver's last chosen landing language. */
export const ERROR_SCREEN_LANGUAGE_PREFERENCE_KEY = 'rettx_landing_lang';

/**
 * Narrow an arbitrary language-ish value to a supported error-screen locale.
 * Accepts region/script subtags (`es-ES`, `en_GB`) and returns `undefined` for
 * anything unsupported so callers can fall back to English.
 */
export function normalizeErrorScreenLocale(
  value: unknown
): ErrorScreenLocale | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const baseLanguage = value
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .split('-', 1)[0];
  return baseLanguage === 'en' || baseLanguage === 'es'
    ? baseLanguage
    : undefined;
}

/**
 * Resolve the active error-screen locale from failure-tolerant sources, in
 * descending order of reliability, always ending at the English fallback.
 * Never throws.
 */
export function resolveErrorScreenLocale(
  documentRef: Document | undefined
): ErrorScreenLocale {
  const readers: Array<() => unknown> = [
    () =>
      documentRef?.documentElement?.getAttribute(
        ERROR_SCREEN_LANGUAGE_ATTRIBUTE
      ),
    () =>
      documentRef?.defaultView?.localStorage?.getItem(
        ERROR_SCREEN_LANGUAGE_PREFERENCE_KEY
      ),
    () => documentRef?.defaultView?.navigator?.language,
  ];

  for (const read of readers) {
    try {
      const value = read();
      if (value === null || value === undefined || value === '') {
        continue;
      }
      return normalizeErrorScreenLocale(value) ?? 'en';
    } catch {
      // Continue through progressively weaker locale sources.
    }
  }
  return 'en';
}
